import { inject, injectable } from 'inversify';
import ym from 'js.api';
import { typestate } from 'typestate';

import { getConfig } from './ConfigLoader';
import { isLatLngBoundsContext, isLatLngLiteral, isResultEntry, isSearchEntries } from './core/guards';
import { isNotNull } from './core/guards';
import { lteDevice } from './core/responsive';
import { DetailsStorage } from './core/storage/DetailsStorage';
import { RoutingStorage } from './core/storage/RoutingStorage';
import { SearchStorage } from './core/storage/SearchStorage';
import { ContainerTypes } from './core/types/ContainerTypes';
import { SearchTypes } from './core/types/SearchTypes';
import { pointNeqZero, writeUrlParams } from './core/util';
import {
	hasBranches,
	hasOptionalRequestData,
	isAddressRequest,
	isIdRequest,
	isLocationRequest,
	isPositionRequest,
	isRectangleRequest
} from './interfaces/search/RequestData';
import { isCountryRequest } from './interfaces/search/RequestData';
import { Routing, RoutingInjectable } from './interfaces/webservices/Routing';
import {
	SearchByAddress,
	SearchByCountry,
	SearchById,
	SearchByLocation,
	SearchByPosition,
	SearchByRectangle,
	SearchInjectable
} from './interfaces/webservices/SearchResults';

import FiniteStateMachine = typestate.FiniteStateMachine;

export enum ActionStates {
	Start,
	Initialize,
	Idle,
	Lock,
	Error,

	SearchId,
	SearchFilter,
	SearchInput,
	SearchMap,

	CallDetails,
	UpdateDetails,

	CallResults,
	UpdateResults,

	CallRouting,
	UpdateRouting,

	UpdateMap,

	HomePage
}

@injectable()
export class Actions implements RoutingInjectable, SearchInjectable {
	@inject(SearchTypes.Routing)
	public routing!: Routing;
	@inject(SearchTypes.Id)
	public id!: SearchById;
	@inject(SearchTypes.Address)
	public address!: SearchByAddress;
	@inject(SearchTypes.Location)
	public location!: SearchByLocation;
	@inject(SearchTypes.Position)
	public position!: SearchByPosition;
	@inject(SearchTypes.Rectangle)
	public rectangle!: SearchByRectangle;
	@inject(SearchTypes.Country)
	public country!: SearchByCountry;

	@inject(ContainerTypes.SearchStorage)
	public searchStorage!: SearchStorage;
	@inject(ContainerTypes.RoutingStorage)
	public routingStorage!: RoutingStorage;
	@inject(ContainerTypes.DetailsStorage)
	public detailsStorage!: DetailsStorage;

	private fsm: FiniteStateMachine<ActionStates>;

	constructor() {
		this.fsm = new FiniteStateMachine<ActionStates>(ActionStates.Start);

		this.fsm.from(ActionStates.Start).to(ActionStates.Lock, ActionStates.Initialize, ActionStates.HomePage);

		this.fsm
			.from(ActionStates.Initialize)
			.to(
				ActionStates.Initialize,
				ActionStates.Lock,
				ActionStates.SearchId,
				ActionStates.SearchInput,
				ActionStates.SearchMap,
				ActionStates.CallResults,
				ActionStates.CallDetails
			);

		this.fsm
			.from(ActionStates.Error)
			.to(
				ActionStates.Start,
				ActionStates.Idle,
				ActionStates.Lock,
				ActionStates.SearchId,
				ActionStates.SearchFilter,
				ActionStates.SearchInput,
				ActionStates.SearchMap,
				ActionStates.CallResults,
				ActionStates.CallDetails,
				ActionStates.CallRouting,
				ActionStates.UpdateMap
			);
		this.fsm.fromAny(ActionStates).to(ActionStates.Error);

		this.fsm
			.from(ActionStates.Lock)
			.to(
				ActionStates.Initialize,
				ActionStates.UpdateMap,
				ActionStates.SearchInput,
				ActionStates.SearchFilter,
				ActionStates.CallResults,
				ActionStates.CallDetails,
				ActionStates.CallRouting
			);

		this.fsm
			.from(ActionStates.Idle)
			.to(
				ActionStates.Initialize,
				ActionStates.Lock,
				ActionStates.SearchId,
				ActionStates.SearchFilter,
				ActionStates.SearchInput,
				ActionStates.SearchMap,
				ActionStates.CallResults,
				ActionStates.CallDetails,
				ActionStates.CallRouting
			);

		this.fsm
			.from(ActionStates.SearchFilter, ActionStates.SearchInput, ActionStates.SearchMap, ActionStates.CallResults)
			.to(ActionStates.UpdateResults, ActionStates.Lock);

		this.fsm
			.from(ActionStates.CallDetails)
			.to(ActionStates.UpdateDetails, ActionStates.CallDetails, ActionStates.CallResults);

		this.fsm
			.from(ActionStates.UpdateResults, ActionStates.UpdateDetails, ActionStates.UpdateRouting)
			.to(ActionStates.UpdateMap, ActionStates.CallDetails, ActionStates.CallResults);

		this.fsm
			.from(ActionStates.UpdateDetails, ActionStates.UpdateRouting)
			.to(ActionStates.CallDetails, ActionStates.CallRouting, ActionStates.CallResults, ActionStates.SearchInput);

		this.fsm
			.from(ActionStates.CallRouting)
			.to(
				ActionStates.UpdateRouting,
				ActionStates.CallResults,
				ActionStates.CallDetails,
				ActionStates.CallRouting
			);

		this.fsm.from(ActionStates.SearchId).to(ActionStates.UpdateDetails);

		this.fsm.from(ActionStates.UpdateMap).to(ActionStates.Idle, ActionStates.Lock);

		this.fsm.fromAny(ActionStates).to(ActionStates.HomePage);
		this.fsm.from(ActionStates.HomePage).to(ActionStates.Initialize, ActionStates.SearchInput);

		let map: ym.provider.MapContext;
		let branches!: string[];
		let dataFilters = '';
		let changedBranches = false;
		let distanceTo: ym.provider.LatLngLiteral;

		this.on<ym.provider.MapContext>(ActionStates.Initialize, (from, event?: any) => {
			if (event) {
				if (
					from === ActionStates.Start ||
					from === ActionStates.HomePage ||
					from === ActionStates.Initialize ||
					from === ActionStates.Idle
				) {
					map = event.map;
					if (getConfig().searchRequest.initialGeolocation) {
						if (navigator.geolocation) {
							navigator.geolocation.getCurrentPosition(
								position => {
									this.go(ActionStates.SearchInput, {
										type: 'Position',
										center: {
											lat: position.coords.latitude,
											lng: position.coords.longitude
										}
									});
								},
								() => {
									if (getConfig().searchRequest.initialSearch) {
										this.searchStorage.isInitial = false;
										this.go(ActionStates.SearchInput, getConfig().searchRequest.initialSearch);
									}
								},
								{
									enableHighAccuracy: true,
									timeout: 10000
								}
							);
						}
						return;
					}
					this.go(ActionStates.Lock, {});
				}
			}
		});

		this.on(ActionStates.Lock, from => {
			if (from === ActionStates.SearchFilter) {
				changedBranches = true;
			}
		});

		this.on(ActionStates.Idle, () => {
			if (this.searchStorage.isInitial) {
				this.searchStorage.isInitial = false;
			}
			if (changedBranches) {
				changedBranches = false;
				writeUrlParams({ id: null });
				this.go(ActionStates.SearchMap, { type: 'Rectangle' });
			}
		});

		this.on(ActionStates.SearchInput, async (from, event: any) => {
			let result = {};
			if (hasBranches(event)) {
				branches = event.branches;
			} else {
				branches = getConfig().searchRequest.branches;
			}
			const requestData = {
				...getConfig().searchRequest,
				...event,
				branches,
				dataFilters
			};

			if (hasOptionalRequestData(requestData)) {
				requestData.fitBounds = true;
			}

			try {
				if (isLocationRequest(requestData)) {
					result = await this.location.find(requestData as never);
				} else if (isAddressRequest(requestData)) {
					result = await this.address.find(requestData as never);
				} else if (isPositionRequest(requestData)) {
					result = await this.position.find(requestData as never);
				} else if (isCountryRequest(requestData)) {
					result = await this.country.find(requestData as never);
				} else if (isIdRequest(requestData)) {
					result = await this.id.find(requestData as never);
				}

				if (isSearchEntries(result) && isLatLngLiteral(result.center)) {
					distanceTo = result.center;
				}

				this.go(ActionStates.UpdateResults, {
					...result,
					fitBounds: true
				});
			} catch (err) {
				this.go(ActionStates.Error, err);
			}
		});

		this.on(ActionStates.SearchFilter, async (from, event: any) => {
			let result = {};
			if (hasBranches(event)) {
				branches = event.branches;
			} else {
				branches = getConfig().searchRequest.branches;
			}

			dataFilters = event.dataFilters;

			if (from === ActionStates.Lock) {
				this.go(ActionStates.Lock, {});
				return;
			}

			const requestData = {
				...getConfig().searchRequest,
				...event,
				branches
			};

			if (hasOptionalRequestData(requestData) && isLatLngLiteral(distanceTo)) {
				requestData.distanceTo = distanceTo;
				requestData.fitBounds = false;
			}

			try {
				writeUrlParams(requestData as never);

				if (isLocationRequest(requestData)) {
					result = await this.location.find(requestData as never);
				} else if (isAddressRequest(requestData)) {
					result = await this.address.find(requestData as never);
				} else if (isPositionRequest(requestData)) {
					result = await this.position.find(requestData as never);
				} else if (isRectangleRequest(requestData)) {
					if (!requestData.bounds) {
						requestData.bounds = this.createBounds(event, map);
					}
					result = await this.rectangle.find(requestData as never);
				}

				this.go(ActionStates.UpdateResults, {
					...result,
					fitBounds: false
				});
			} catch (err) {
				this.go(ActionStates.Error, err);
			}
		});

		this.on(ActionStates.SearchMap, async (from, event: any) => {
			let result = {};

			const bounds: ym.provider.LatLngBoundsLiteral = this.createBounds(event, map);
			if (!branches) {
				branches = getConfig().searchRequest.branches;
			}

			const requestData = {
				...getConfig().searchRequest,
				bounds,
				...event,
				branches,
				dataFilters
			};

			if (hasOptionalRequestData(requestData) && isLatLngLiteral(distanceTo)) {
				requestData.distanceTo = distanceTo;
				requestData.fitBounds = false;
			}

			try {
				writeUrlParams(requestData as never);

				if (isRectangleRequest(requestData)) {
					result = await this.rectangle.find(requestData as never);
				}

				this.go(ActionStates.UpdateResults, {
					...result,
					fitBounds: false
				});
			} catch (err) {
				this.go(ActionStates.Error, err);
			}
		});

		this.on(ActionStates.CallDetails, (from, event?: { id: string }) => {
			if (
				isNotNull(event) &&
				isSearchEntries(this.searchStorage.results) &&
				this.searchStorage.results.entries.length
			) {
				const entry = this.searchStorage.results.entries.find(
					entry => isResultEntry(entry) && entry.idDecoded === (event as any).id
				);
				if (isResultEntry(entry)) {
					this.detailsStorage.entry = entry;
					this.routingStorage.entry = entry;
				}
			}
		});

		this.on(ActionStates.Error, (from, event) => {
			console.error(event);
			this.go(ActionStates.Idle, {});
		});
	}

	get currentState(): ActionStates {
		return this.fsm.currentState;
	}

	public canGo(state: ActionStates): boolean {
		return this.fsm.canGo(state);
	}

	public go<T>(state: ActionStates, event?: T): this {
		// tslint:disable-next-line:no-console
		console.log(ActionStates[this.fsm.currentState], '->', ActionStates[state || 0]);
		try {
			this.fsm.go(state, event);
		} catch (err) {
			console.error(
				'Failed transition:',
				ActionStates[this.fsm.currentState],
				'->',
				ActionStates[state || 0],
				err
			);
		}
		return this;
	}

	public goIf<T>(state: ActionStates, predicate: (state: ActionStates) => boolean, event?: T): this {
		const isValid = predicate(this.fsm.currentState);
		if (isValid) this.go(state, event);
		return this;
	}

	public goIfStateChanged<T>(state: ActionStates, event?: T): this {
		return this.goIf(state, s => this.isCurrentState(s), event);
	}

	public isCurrentState(state: ActionStates): boolean {
		const r = this.fsm.is(state);
		return r;
	}

	public on<T>(type: ActionStates, callback: (from?: ActionStates, event?: T) => void): this {
		this.fsm.on(type, callback);
		return this;
	}

	public is(type: ActionStates): boolean {
		return this.fsm.is(type);
	}

	private createBounds(event: any, map: ym.provider.MapContext): ym.provider.LatLngBoundsLiteral {
		const bounds: ym.provider.LatLngBoundsLiteral = {
			northEast: { lat: 0, lng: 0 },
			southWest: { lat: 0, lng: 0 }
		};

		if (isRectangleRequest(event)) {
			let nw: ym.provider.LatLngLiteral = { lat: 0, lng: 0 };
			let se: ym.provider.LatLngLiteral = { lat: 0, lng: 0 };

			if (isLatLngLiteral(event.bounds)) {
				nw = {
					lat: event.bounds.northEast.lat,
					lng: event.bounds.southWest.lng
				};
				se = {
					lat: event.bounds.southWest.lat,
					lng: event.bounds.northEast.lng
				};
			} else if (isLatLngBoundsContext(event.bounds)) {
				nw = event.bounds.getNorthWest();
				se = event.bounds.getSouthEast();
			} else {
				const bounds = map.getBounds();

				// calculate search offset on map move
				nw = bounds.getNorthWest();
				se = bounds.getSouthEast();
			}
			bounds.northEast.lat = nw.lat;
			bounds.northEast.lng = se.lng;
			bounds.southWest.lat = se.lat;
			bounds.southWest.lng = nw.lng;
		}

		return bounds;
	}
}
